import React, { useState } from "react";
import EmailDeliverySection from "./email_delivery_section";

import {
  AlertBanner,
  AvailabilityPager,
  EmailDetailsSection,
  ErrorList,
  ExistingAppointmentsNotice,
  PillGroup,
  SectionIntro,
  type BaseInviteFormState,
  type Option,
  type PaymentClassificationPayload,
  type WorkspaceBase,
  openSchedulingPreview,
  responseErrorMessage,
} from "./shared";

interface OperationInviteFormState extends BaseInviteFormState {
  scheduledSendAt: string;
  paymentClassification: string;
  locationFilter: string;
}

interface PendingInvite {
  patientName: string;
  patientPath: string;
  schedulingPageViewed: boolean;
}

interface OperationDay {
  key: string;
  date: string;
  dateLabel: string;
  location: string;
  locationLabel: string;
  remainingCasesLabel: string;
  pendingInvitesLabel?: string | null;
  pendingInvites: PendingInvite[];
}

interface OperationAvailabilityDate {
  date: string;
  days: OperationDay[];
}

interface OperationAvailability {
  startDate: string;
  endDate: string;
  previousStartDate?: string | null;
  nextStartDate: string;
  rangeLabel: string;
  emptyMessage: string;
  locationFilter: string;
  selectedDayKeys: string[];
  locationFilterOptions: Option[];
  dates: OperationAvailabilityDate[];
}

interface OperationSchedulingInviteWorkspacePayload extends WorkspaceBase<OperationInviteFormState> {
  payment: PaymentClassificationPayload;
  availability: OperationAvailability;
  rolloutPaused: boolean;
}

interface WorkspaceResponse {
  workspace?: OperationSchedulingInviteWorkspacePayload;
  errors?: string[];
  redirectPath?: string;
  notice?: string;
}

function selectedKeysAfterToggle(selectedKeys: string[], key: string, checked: boolean) {
  const selectedSet = new Set(selectedKeys);

  if (checked) {
    selectedSet.add(key);
  } else {
    selectedSet.delete(key);
  }

  return Array.from(selectedSet);
}

function ChevronIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-4 w-4 transition group-open:rotate-180" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="m5 7.5 5 5 5-5" />
    </svg>
  );
}

function ClassificationField({
  id,
  label,
  value,
  options,
  evidenceText,
  linkLabel,
  linkPath,
  emptyOption,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: Option[];
  evidenceText: string;
  linkLabel: string;
  linkPath: string;
  emptyOption?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex min-w-0 items-end gap-1.5">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
          {label}
        </label>
        <select id={id} className="app-select mt-1" required value={value} onChange={(event) => onChange(event.target.value)}>
          {emptyOption ? <option value="">{emptyOption}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <details className="group relative mb-0.5 shrink-0">
        <summary
          title={evidenceText}
          aria-label={`Show source for ${label}`}
          className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md border border-neutral-200 text-xs font-bold text-neutral-500 marker:hidden hover:border-neutral-300 hover:text-neutral-700 [&::-webkit-details-marker]:hidden dark:border-neutral-800 dark:text-neutral-400 dark:hover:border-neutral-700 dark:hover:text-neutral-200"
        >
          i
        </summary>
        <div className="invisible absolute right-0 top-9 z-30 w-[min(20rem,calc(100vw-3rem))] rounded-lg border border-neutral-200 bg-white p-3 text-sm leading-5 text-neutral-600 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-open:visible group-open:opacity-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300">
          <p>{evidenceText}</p>
          <a
            href={linkPath}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block font-semibold text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
          >
            {linkLabel}
          </a>
        </div>
      </details>
    </div>
  );
}

export default function OperationSchedulingInviteWorkspace(props: OperationSchedulingInviteWorkspacePayload) {
  const [form, setForm] = useState<OperationInviteFormState>(props.form);
  const [scheduleForLater, setScheduleForLater] = useState(Boolean(props.form.scheduledSendAt));
  const [availability, setAvailability] = useState<OperationAvailability>(props.availability);
  const [selectedDayKeys, setSelectedDayKeys] = useState<string[]>(props.availability.selectedDayKeys);
  const [errors, setErrors] = useState<string[]>(props.errors || []);
  const [flash, setFlash] = useState<{ tone: "error" | "success"; message: string } | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateForm = <Key extends keyof OperationInviteFormState>(field: Key, value: OperationInviteFormState[Key]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyWorkspace = (workspace: OperationSchedulingInviteWorkspacePayload, source: "availability" | "submit") => {
    setAvailability(workspace.availability);
    setErrors(workspace.errors || []);

    if (source === "submit") {
      setForm(workspace.form);
      setSelectedDayKeys(workspace.availability.selectedDayKeys);
    } else {
      setForm((current) => ({ ...current, locationFilter: workspace.form.locationFilter }));
    }
  };

  const loadAvailability = async (startDate: string, locationFilter = form.locationFilter) => {
    setLoadingAvailability(true);
    setFlash(null);

    const url = new URL(props.availabilityUrl, window.location.origin);
    url.searchParams.set("operation_scheduling_invite[availability_start]", startDate);
    url.searchParams.set("operation_scheduling_invite[location_filter]", locationFilter);
    selectedDayKeys.forEach((dayKey) => {
      url.searchParams.append("operation_scheduling_invite[selected_day_keys][]", dayKey);
    });

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const payload = (await response.json().catch(() => ({}))) as WorkspaceResponse;

      if (!response.ok || !payload.workspace) {
        throw new Error(responseErrorMessage(payload, "Unable to load availability."));
      }

      applyWorkspace(payload.workspace, "availability");
    } catch (error) {
      setFlash({ tone: "error", message: error instanceof Error ? error.message : "Unable to load availability." });
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleLocationFilterChange = (value: string) => {
    updateForm("locationFilter", value);
    void loadAvailability(availability.startDate, value);
  };

  const handleDayToggle = (dayKey: string, checked: boolean) => {
    setSelectedDayKeys((current) => selectedKeysAfterToggle(current, dayKey, checked));
  };

  const serializedInvite = (): Record<string, unknown> => ({
    recipient_email: form.recipientEmail,
    expires_at: form.expiresAt,
    scheduled_send_at: scheduleForLater ? form.scheduledSendAt : "",
    email_subject: form.emailSubject,
    email_body: form.emailBody,
    patient_state_classification: form.patientStateClassification,
    payment_classification: form.paymentClassification,
    availability_start: availability.startDate,
    location_filter: form.locationFilter,
    selected_day_keys: selectedDayKeys,
  });

  const submitInvite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);
    setFlash(null);

    try {
      const response = await fetch(props.submitUrl, {
        method: props.submitMethod,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": props.authenticityToken,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          operation_scheduling_invite: serializedInvite(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as WorkspaceResponse;

      if (!response.ok) {
        if (payload.workspace) applyWorkspace(payload.workspace, "submit");
        throw new Error(responseErrorMessage(payload, "Unable to save operation scheduling link."));
      }

      if (payload.redirectPath) {
        window.location.assign(payload.redirectPath);
        return;
      }

      setFlash({ tone: "success", message: payload.notice || "Operation scheduling link saved." });
    } catch (error) {
      setFlash({ tone: "error", message: error instanceof Error ? error.message : "Unable to save operation scheduling link." });
    } finally {
      setSubmitting(false);
    }
  };

  const patientStateEvidence = props.patientState.evidenceByClassification[form.patientStateClassification] || "No source information is available.";
  const paymentEvidence = props.payment.evidenceByClassification[form.paymentClassification] || "Choose the payment type for this invite.";

  return (
    <form className="space-y-4" onSubmit={submitInvite}>
      <AlertBanner flash={flash} />
      <ErrorList errors={errors} />
      {props.editNotice ? <AlertBanner flash={{ tone: "warning", message: props.editNotice }} /> : null}
      {props.rolloutPaused && !props.editing ? (
        <AlertBanner flash={{ tone: "warning", message: "Operation self-scheduling is paused. Enable it from Operations → Self-scheduling before sending a link." }} />
      ) : null}
      {props.outstandingInviteWarning ? <AlertBanner flash={{ tone: "warning", message: props.outstandingInviteWarning }} /> : null}

      {!props.editing ? <section className="app-surface overflow-visible">
        <div className="grid grid-cols-2 gap-3 p-3">
          <ClassificationField
            id="scheduling-invite-patient-state"
            label="Location"
            value={form.patientStateClassification}
            options={props.patientState.options}
            evidenceText={patientStateEvidence}
            linkLabel={props.patientState.linkLabel}
            linkPath={props.patientState.linkPath}
            onChange={(value) => updateForm("patientStateClassification", value)}
          />
          <ClassificationField
            id="operation-scheduling-payment-classification"
            label="Payment type"
            value={form.paymentClassification}
            options={props.payment.options}
            evidenceText={paymentEvidence}
            linkLabel={props.payment.linkLabel}
            linkPath={props.payment.linkPath}
            emptyOption="Select"
            onChange={(value) => updateForm("paymentClassification", value)}
          />
        </div>

        <details className="group border-t border-neutral-200/80 dark:border-neutral-800">
          <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-2.5 marker:hidden [&::-webkit-details-marker]:hidden">
            <span className="text-sm font-semibold text-neutral-950 dark:text-white">Invite details</span>
            <span className="min-w-0 flex-1 truncate text-sm text-neutral-500 dark:text-neutral-400">
              {form.recipientEmail || "No recipient email"}
            </span>
            <span className="text-neutral-400 dark:text-neutral-500"><ChevronIcon /></span>
          </summary>
          <div className="border-t border-neutral-200/80 p-3 dark:border-neutral-800">
            <p className="mb-3 text-sm text-neutral-500 dark:text-neutral-400">
              The email body can include <code>{props.linkPlaceholder}</code>. If omitted, the scheduling link is appended automatically.
            </p>
            <EmailDetailsSection
              form={form}
              linkPlaceholder={props.linkPlaceholder}
              onChange={updateForm}
              showHeading={false}
            />
          </div>
        </details>
      </section> : null}

      <ExistingAppointmentsNotice appointments={props.existingAppointments} appointmentType="operation" />

      <section className={`app-surface p-4 ${loadingAvailability ? "opacity-70" : ""}`} aria-busy={loadingAvailability}>
        <SectionIntro kicker="Availability" title="Choose what this patient can book">
          Patients can select an operation day and location, but not a specific time.
        </SectionIntro>

        <PillGroup
          label="Location eligibility"
          options={availability.locationFilterOptions}
          value={form.locationFilter}
          onChange={handleLocationFilterChange}
        />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-200/80 bg-neutral-50 px-3 py-2 text-sm dark:border-neutral-800 dark:bg-neutral-900/70">
          <span className="font-medium text-neutral-700 dark:text-neutral-200">
            {selectedDayKeys.length} {selectedDayKeys.length === 1 ? "day" : "days"} selected across all pages and location filters
          </span>
          {selectedDayKeys.length > 0 ? (
            <button type="button" className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200" onClick={() => setSelectedDayKeys([])}>
              Clear selections
            </button>
          ) : null}
        </div>

        {availability.dates.length > 0 ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {availability.dates.map((date) => (
              <section key={date.date}>
                <div className="mt-4 grid gap-2.5">
                  {date.days.map((day) => (
                    <label key={day.key} className="app-check-card">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500 dark:border-neutral-700 dark:bg-neutral-900"
                        checked={selectedDayKeys.includes(day.key)}
                        onChange={(event) => handleDayToggle(day.key, event.target.checked)}
                      />
                      <span>
                        <span className="block font-semibold text-neutral-950 dark:text-white">{day.dateLabel}</span>
                        <span className="mt-1 block text-xs uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                          {day.locationLabel}
                        </span>
                        <span className="mt-1 block text-sm text-neutral-600 dark:text-neutral-300">{day.remainingCasesLabel}</span>
                        {day.pendingInvites.length > 0 ? (
                          <span className="mt-1 block text-sm text-neutral-600 dark:text-neutral-300">
                            {day.pendingInvitesLabel} pending:{" "}
                            {day.pendingInvites.map((invite, index) => (
                              <span key={invite.patientPath}>
                                {index > 0 ? ", " : ""}
                                <a
                                  href={invite.patientPath}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
                                >
                                  {invite.patientName}
                                </a>
                                <span className={invite.schedulingPageViewed ? "ml-1 text-emerald-700 dark:text-emerald-300" : "ml-1 text-amber-700 dark:text-amber-300"}>
                                  ({invite.schedulingPageViewed ? "viewed" : "not viewed"})
                                </span>
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <p className="mt-4 rounded-xl border border-dashed border-neutral-300 px-4 py-5 text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
            {availability.emptyMessage}
          </p>
        )}

        <AvailabilityPager
          rangeLabel={availability.rangeLabel}
          previousStartDate={availability.previousStartDate}
          nextStartDate={availability.nextStartDate}
          loading={loadingAvailability}
          onPage={(startDate) => void loadAvailability(startDate)}
        />
      </section>

      <EmailDeliverySection
        editing={props.editing}
        scheduledSendAt={form.scheduledSendAt}
        scheduleForLater={scheduleForLater}
        onScheduleForLaterChange={setScheduleForLater}
        onChange={(value) => updateForm("scheduledSendAt", value)}
        firstOfferedAppointment="first offered operation day"
      />

      <div className="flex flex-wrap gap-2">
        <button type="submit" className="app-button-primary" disabled={submitting || (props.rolloutPaused && !props.editing)}>
          {submitting ? "Saving..." : !props.editing && scheduleForLater ? "Schedule email" : props.submitLabel}
        </button>
        <button
          type="button"
          className="app-button-secondary"
          disabled={submitting || loadingAvailability}
          onClick={() => openSchedulingPreview(props.previewUrl, props.authenticityToken, serializedInvite())}
        >
          Preview patient experience
        </button>
        <a href={props.cancelPath} className="app-button-secondary">
          Cancel
        </a>
      </div>
    </form>
  );
}
